/**
 * BMM — Clothio Hero Marquee
 * File: bmm-hero-marquee.js
 * Upload to: Assets folder in Shopify theme
 * Loaded with: defer attribute (non-blocking)
 * Mockup ref: Clothio_Homepage_Final.html — HERO section JS
 * ─────────────────────────────────────────────────────────
 *
 * Responsibilities:
 *  1. Pause marquee on desktop hover (if enabled via schema)
 *  2. Never pause on touch/mobile devices
 *  3. Pause when section scrolls out of viewport (saves CPU)
 *  4. Resume when section re-enters viewport
 *  5. Respect prefers-reduced-motion (CSS handles it; JS mirrors)
 */

(function () {
  'use strict';

  /* ── INIT ─────────────────────────────────────────────────────────── */
  function initBmmHero(section) {
    var marquee    = section.querySelector('.bmm-hero__marquee');
    var pauseHover = section.dataset.pauseHover === 'true';

    if (!marquee) return;

    /* ── 1. DETECT TOUCH DEVICE ────────────────────────────────────── */
    /*
      We check once at init. If the device supports touch we treat it
      as mobile regardless of screen width (tablets in portrait, etc.)
    */
    var isTouch = (
      'ontouchstart' in window ||
      navigator.maxTouchPoints > 0 ||
      navigator.msMaxTouchPoints > 0
    );

    /* ── 2. PAUSE ON HOVER (desktop only) ───────────────────────────── */
    if (pauseHover && !isTouch) {
      section.addEventListener('mouseenter', function () {
        marquee.classList.add('is-paused');
      }, { passive: true });

      section.addEventListener('mouseleave', function () {
        marquee.classList.remove('is-paused');
      }, { passive: true });
    }

    /* ── 3. PAUSE WHEN OFF-SCREEN ───────────────────────────────────── */
    /*
      IntersectionObserver pauses the CSS animation when the hero
      is not in the viewport. Saves CPU/battery on long-scroll pages.
    */
    if ('IntersectionObserver' in window) {
      var visibilityObserver = new IntersectionObserver(
        function (entries) {
          entries.forEach(function (entry) {
            if (entry.isIntersecting) {
              marquee.classList.remove('bmm-offscreen');
            } else {
              marquee.classList.add('bmm-offscreen');
            }
          });
        },
        { threshold: 0.05 } /* 5% visible = considered on-screen */
      );
      visibilityObserver.observe(section);
    }

    /* ── 4. REDUCED MOTION ──────────────────────────────────────────── */
    /*
      CSS @media (prefers-reduced-motion) already pauses the animation.
      JS mirrors this so any future JS-driven animation also stops.
    */
    var motionQuery = window.matchMedia
      ? window.matchMedia('(prefers-reduced-motion: reduce)')
      : null;

    if (motionQuery && motionQuery.matches) {
      marquee.classList.add('is-paused');
    }

    if (motionQuery && motionQuery.addEventListener) {
      motionQuery.addEventListener('change', function (e) {
        if (e.matches) {
          marquee.classList.add('is-paused');
        } else if (!isTouch) {
          marquee.classList.remove('is-paused');
        }
      });
    }
  }

  /* ── FIND ALL BMM HERO SECTIONS ON PAGE ─────────────────────────── */
  /*
    Multiple hero sections could theoretically exist (e.g. on a custom
    page template). We initialise each independently.
  */
  function init() {
    var sections = document.querySelectorAll('.bmm-hero[data-section-id]');
    sections.forEach(function (section) {
      initBmmHero(section);
    });
  }

  /* ── RUN ─────────────────────────────────────────────────────────── */
  /*
    Script loaded with defer — DOM is ready when this runs.
    But we also listen for DOMContentLoaded as a safety net.
  */
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  /* ── SHOPIFY THEME EDITOR SUPPORT ───────────────────────────────── */
  /*
    When merchant adds/removes/reorders sections in the theme editor,
    Shopify fires section:load. Re-initialise the specific section.
  */
  document.addEventListener('shopify:section:load', function (event) {
    var section = event.target.querySelector('.bmm-hero[data-section-id]');
    if (section) {
      initBmmHero(section);
    }
  });

})();
